import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import { IRelatedEntity } from '@app/core/model/entities/entity';
import { eq, exists, inSet, orGroup } from '@app/shared/extra/filter-model-builder';
import { getValue } from '@app/shared/extra/utils';
import { ExtendedColDef } from '@app/shared/grid/column-builder';
import {
  ColumnVO,
  DateFilterModel,
  ICombinedSimpleModel,
  NumberFilterModel,
  SortModelItem,
  TextFilterModel
} from 'ag-grid-community';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

export enum FilterKind {
  EQUALS      = 'EQUALS',
  NOT_EQUALS  = 'NOT_EQUALS',
  IN          = 'IN',
  EXISTS      = 'EXISTS',
  OR          = 'OR',
  ELEM_MATCH  = 'ELEM_MATCH',
  TEXT        = 'TEXT',
  NUMBER      = 'NUMBER',
  DATE        = 'DATE'
}

export enum TextFilterType {
  TEXT_CONTAINS =     'TEXT_CONTAINS',
  TEXT_NOT_CONTAINS = 'TEXT_NOT_CONTAINS',
  TEXT_STARTS_WITH =  'TEXT_STARTS_WITH',
  TEXT_ENDS_WITH =    'TEXT_ENDS_WITH',
  TEXT_EQUALS =       'TEXT_EQUALS',
  TEXT_NOT_EQUALS =   'TEXT_NOT_EQUALS',
  TEXT_BLANK =        'TEXT_BLANK',
  TEXT_NOT_BLANK =    'TEXT_NOT_BLANK'
}

export enum NumberFilterType {
  NUMBER_EQUALS =                 'NUMBER_EQUALS',
  NUMBER_NOT_EQUALS =             'NUMBER_NOT_EQUALS',
  NUMBER_GREATER_THAN =           'NUMBER_GREATER_THAN',
  NUMBER_GREATER_THAN_OR_EQUAL =  'NUMBER_GREATER_THAN_OR_EQUAL',
  NUMBER_LESS_THAN =              'NUMBER_LESS_THAN',
  NUMBER_LESS_THAN_OR_EQUAL =     'NUMBER_LESS_THAN_OR_EQUAL',
  NUMBER_BETWEEN =                'NUMBER_BETWEEN',
  NUMBER_BLANK =                  'NUMBER_BLANK',
  NUMBER_NOT_BLANK =              'NUMBER_NOT_BLANK'
}

export enum DateFilterType {
  DATE_EQUALS =       'DATE_EQUALS',
  DATE_NOT_EQUALS =   'DATE_NOT_EQUALS',
  DATE_BEFORE =       'DATE_BEFORE',
  DATE_AFTER =        'DATE_AFTER',
  DATE_BETWEEN =      'DATE_BETWEEN',
  DATE_BLANK =        'DATE_BLANK',
  DATE_NOT_BLANK =    'DATE_NOT_BLANK'
}

type AgTextType = 'contains' | 'notContains' | 'startsWith' | 'endsWith' | 'equals' | 'notEqual' | 'blank' | 'notBlank';
type AgNumberType =
  'equals'
  | 'notEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'inRange'
  | 'blank'
  | 'notBlank';
type AgDateType = 'equals' | 'notEqual' | 'lessThan' | 'greaterThan' | 'inRange' | 'blank' | 'notBlank';

const textFilterLookup: Record<AgTextType, TextFilterType> = {
  contains:    TextFilterType.TEXT_CONTAINS,
  notContains: TextFilterType.TEXT_NOT_CONTAINS,
  startsWith:  TextFilterType.TEXT_STARTS_WITH,
  endsWith:    TextFilterType.TEXT_ENDS_WITH,
  equals:      TextFilterType.TEXT_EQUALS,
  notEqual:    TextFilterType.TEXT_NOT_EQUALS,
  blank:       TextFilterType.TEXT_BLANK,
  notBlank:    TextFilterType.TEXT_NOT_BLANK
};

const numberFilterLookup: Record<AgNumberType, NumberFilterType> = {
  equals:              NumberFilterType.NUMBER_EQUALS,
  notEqual:            NumberFilterType.NUMBER_NOT_EQUALS,
  greaterThan:         NumberFilterType.NUMBER_GREATER_THAN,
  greaterThanOrEqual:  NumberFilterType.NUMBER_GREATER_THAN_OR_EQUAL,
  lessThan:            NumberFilterType.NUMBER_LESS_THAN,
  lessThanOrEqual:     NumberFilterType.NUMBER_LESS_THAN_OR_EQUAL,
  inRange:             NumberFilterType.NUMBER_BETWEEN,
  blank:               NumberFilterType.NUMBER_BLANK,
  notBlank:            NumberFilterType.NUMBER_NOT_BLANK
};

const dateFilterLookup: Record<AgDateType, DateFilterType> = {
  equals:       DateFilterType.DATE_EQUALS,
  notEqual:     DateFilterType.DATE_NOT_EQUALS,
  lessThan:     DateFilterType.DATE_BEFORE,
  greaterThan:  DateFilterType.DATE_AFTER,
  inRange:      DateFilterType.DATE_BETWEEN,
  blank:        DateFilterType.DATE_BLANK,
  notBlank:     DateFilterType.DATE_NOT_BLANK
};

type TextFilter = {
  type: TextFilterType;
  filter?: String;
};

type NumberFilter = {
  type: NumberFilterType;
  filter?: number;
  filterTo?: number;
};

type DateFilter = {
  type: DateFilterType;
  dateFrom?: string;
  dateTo?: string;
};

export type Filter = {
  kind: FilterKind;
  field?: string;
  value?: any;
  values?: any[];
  conditions?: Filter[];
  textFilter?: TextFilter
  numberFilter?: NumberFilter
  dateFilter?: DateFilter
};

export interface SelectParams {
  start?: number;
  count?: number;
  sorts?: Sort[];
  locale?: string;
  groups?: string[];
  groupKeys?: any[];
  filters?: Filter[];
  search?: string;
  aggregations?: Aggregation[];
}


interface Sort {
  field: string;
  direction?: 'ASC' | 'DESC';
}

export interface Aggregation {
  field: string;
  operation: AggregationOperation;
}

export type AggregationOperation = 'AVG' | 'COUNT' | 'MAX' | 'MIN' | 'SUM' | 'FIRST' | 'ALL' | 'ANY' | 'NONE';

/**
 * Transforms the sort model by replacing colId with the full field path.
 * If `ag-Grid-AutoColumn` is present, it is filtered to avoid an unexpected error.
 *
 * @param agSortModel - SortModel array to convert to API sorts.
 * @param columnDefs - Column definitions from the grid API.
 * @return A list of API Sort object.
 */
export function getConvertedAgSortModel(agSortModel: SortModelItem[], columnDefs: ExtendedColDef[]): Sort[] {
  return agSortModel.filter(item => item.colId !== 'ag-Grid-AutoColumn')
    .map(item => {
      const colDef = columnDefs.find(col => col.colId === item.colId);
      let field = colDef.fieldConfig.fieldPath.join('.');
      if (colDef.fieldConfig.field.fieldType === FieldTypeEnum.ACTOR) field += '.name';
      if (colDef.fieldConfig.field.isRelatedEntity) field += '.displayName';
      return <Sort>{
        field,
        direction: item.sort.toUpperCase()
      };
    });
}

/**
 * Transforms the value columns by replacing id with the full field path.
 *
 * @param valueCols - Contains data of AG columns (aggregations...)
 * @param columnDefs - Column definitions from the grid API.
 */
export function getConvertedAgValueCols(valueCols: ColumnVO[], columnDefs: ExtendedColDef[]): Aggregation[] {
  return valueCols.map(valueCol => {
    const colDef = columnDefs.find(col => col.colId === valueCol.id);
    let field = colDef.fieldConfig.fieldPath.join('.');
    if (colDef.fieldConfig.field.fieldType === FieldTypeEnum.ACTOR) field += '.name';
    if (colDef.fieldConfig.field.isRelatedEntity && !colDef.fieldConfig.field.isArray) field += '.displayName';
    return <Aggregation>{
      field,
      operation: valueCol.aggFunc.toUpperCase()
    };
  });
}

/**
 * Generates additional aggregations for fields that have CSS rules dependent on other fields.
 * When performing a grouped query, these dependent fields must be included in the aggregation so that CSS conditions based on other fields can be evaluated correctly.
 *
 * @param columnDefs - Column definitions from the grid API.
 * @return An array of Aggregation objects
 */
export function getCssDependentAggregations(columnDefs: ExtendedColDef[]): Aggregation[] {
  return columnDefs.reduce<Aggregation[]>((acc, colDef) => {
    const customCss = colDef.fieldConfig?.customOptions?.customCss;
    if (!customCss) return acc;

    const dependentFields = Object.values(customCss)
      .flatMap(stylesConditions =>
        stylesConditions.flatMap(stylesCondition =>
          stylesCondition.conditions.map(cond => cond.field)
        )
      )
      .filter(field => field != null);

    dependentFields.forEach(field => {
      if (!acc.some(a => a.field === field)) {
        acc.push({field: field, operation: 'FIRST'});
      }
    });

    return acc;
  }, []);
}

/**
 * Transforms the row group columns by replacing id with the full field path.
 *
 * @param rowGroupCols - Row group columns from the request object.
 * @param columnDefs - Column definitions from the grid API.
 * @return - Transformed row group columns.
 */
export function getConvertedAgRowGroupCols(rowGroupCols: ColumnVO[], columnDefs: ExtendedColDef[]): string[] {
  return rowGroupCols.map(rowGroupCol => {
    const colDef = columnDefs.find(col => col.colId === rowGroupCol.id);
    let fieldPath = colDef.fieldConfig.fieldPath.join('.');
    if (colDef.fieldConfig.field.fieldType === FieldTypeEnum.ACTOR) fieldPath += '.name';
    if (colDef.fieldConfig.field.isRelatedEntity && !colDef.fieldConfig.field.isArray) fieldPath += '.displayName';
    return fieldPath;
  });
}

/**
 * Convert AG-Grid group keys into their corresponding values to be sent to API.
 *
 * Behavior:
 * 1. "__NULL__" is a special flag mapped to `null`.
 * 2. For nested groups (e.g. "actor.name"), return the nested value inside group key.
 * 3. For simple (non-nested) groups, the key is returned unchanged.
 *
 * @param groupKeys - The raw keys provided by AG-Grid for each group level.
 * @param groups - The field paths used for grouping (e.g. `["__NULL__", "actor.name", "status"]`).
 * @param colDefs - Column definitions from the grid API.
 * @return - An array of converted grid API `groupKeys`.
 */
export function getConvertedAgGroupKeys(groupKeys: unknown[], groups: string[], colDefs: ExtendedColDef[]): unknown[] {
  return groupKeys.map((key, i) => {
    // For "__NULL__" flag, return null.
    if (key === '__NULL__') return null;
    const groupPath = groups[i].split('.');

    // For direct group, return the key as is.
    if (groupPath.length === 1) return key;
    const field = colDefs.find(col => col.colId === groupPath[1])?.fieldConfig?.field;

    // For related entity fields that are not array like, extract the display name.
    if (field?.isRelatedEntity && !field.isArray) {
        const nestedKey = (key as Partial<IRelatedEntity>).displayName;
        return (nestedKey === '__NULL__') ? null : nestedKey;
    }

    // For other objects that are not arrays, extract the value.
    if (typeof key === 'object' && !Array.isArray(key)) {
      groupPath.shift();
      return getValue((key as Record<string, any>), groupPath);
    }

    // For any other cases, return the key as is.
    return key;
  });
}

/**
 * Transforms the filter model by replacing colId with the full field path and handling set filters.
 *
 * Converts AG Grid's filterModel object into a standardized Filter[]:
 * - resolves colId to full field path (+ '.displayName' for related entities)
 * - maps AG Grid kinds (set, text, number, date) to schema
 * - normalizes set value '-' to '' (empty string)
 *
 * @param agFilterModel - Filter model from the request object.
 * @param columnDefs - Column definitions from the grid API.
 * @return - Transformed filter model.
 */
export function getConvertedAgFilterModel(agFilterModel: Record<string, any>, columnDefs: ExtendedColDef[]): Filter[] {
  if (!agFilterModel || typeof agFilterModel !== 'object') return [];

  return Object.keys(agFilterModel).map(colId => {
    const agFilter = agFilterModel[colId];
    const colDef = columnDefs.find(col => col.colId === colId);
    let newFieldPath = colDef.fieldConfig.fieldPath.join('.');
    if (colDef.fieldConfig.field.fieldType === FieldTypeEnum.ACTOR) newFieldPath += '.actorId';
    if (colDef.fieldConfig.field.isRelatedEntity) newFieldPath += '.displayName';

    // Convert ag filters to API filters
    switch (agFilter?.filterType) {
      case 'set':
        return convertAgSetItem(newFieldPath, agFilter);
      case 'text':
        return convertAgTextItem(newFieldPath, agFilter);
      case 'number':
        return convertAgNumberItem(newFieldPath, agFilter);
      case 'date':
        return convertAgDateItem(newFieldPath, agFilter);
      default:
        console.warn(`Update required : Unsupported filter type ${agFilter?.filterType}.`);
    }
  }).flat();
}

/**
 * Converts an AG Grid set filter object into an API Filter.
 * Handles special cases where the filter includes empty or null values.
 *
 * @param field The field name associated with the filter.
 * @param agFilter The AG Grid set filter object containing the selected values.
 * @return A Filter object.
 */
function convertAgSetItem(field: string, agFilter: any): Filter {
  const raw = Array.isArray(agFilter.values) ? agFilter.values : [];
  const values: any[] = raw.map(v => (v === '-' ? '' : v));
  if (values.some(v => v === null || v === undefined || v === '')) {
    return orGroup(inSet(field, values), exists(field, false), eq(field, []));
  } else {
    return inSet(field, values);
  }
}

/**
 * Converts an AG Grid text filter object into an API Filter.
 * Handles both single and compound (AND/OR) text filter conditions.
 *
 * @param field The field name associated with the filter.
 * @param agFilter The AG Grid text filter object containing type, operator, and conditions.
 * @return An array of Filter objects representing the text filter(s).
 */
function convertAgTextItem(field: string, agFilter: TextFilterModel | ICombinedSimpleModel<TextFilterModel>): Filter[] {
  // Multiple conditions on filters
  if (isCombinedSimpleModel(agFilter)) {
    const conditions = agFilter.conditions.map(it => ({
      kind: FilterKind.TEXT,
      field,
      textFilter: { type: textFilterLookup[it.type], filter: it.filter }
    }));

    if (agFilter.operator === 'AND') {
      return conditions;
    } else {
      return [orGroup(...conditions)];
    }
  }

  // Single condition
  return [{
    kind: FilterKind.TEXT,
    field,
    textFilter: {
      type: textFilterLookup[agFilter.type],
      filter: agFilter.filter
    }
  }];
}

/**
 * Converts an AG Grid number filter object into an API Filter.
 * Handles both single and compound (AND/OR) numeric filter conditions.
 *
 * @param field The field name associated with the filter.
 * @param agFilter The AG Grid number filter object containing type, operator, and conditions.
 * @return An array of Filter objects representing the numeric filter(s).
 */
function convertAgNumberItem(field: string, agFilter: NumberFilterModel | ICombinedSimpleModel<NumberFilterModel>): Filter[] {
  // Multiple conditions on filters
  if (isCombinedSimpleModel(agFilter)) {
    const conditions = agFilter.conditions.map(it => ({
      kind: FilterKind.NUMBER,
      field,
      numberFilter: { type: numberFilterLookup[it.type], filter: it.filter, filterTo: it.filterTo }
    }));

    if (agFilter.operator === 'AND') {
      return conditions;
    } else {
      return [orGroup(...conditions)];
    }
  }

  // Single condition
  return [{
    kind: FilterKind.NUMBER,
    field,
    numberFilter: {
      type: numberFilterLookup[agFilter.type],
      filter: agFilter.filter,
      filterTo: agFilter.filterTo
    }
  }];
}

/**
 * Converts an AG Grid date filter.
 * Handles both single and compound (AND/OR) filter conditions.
 *
 * @param field The field name associated with the filter.
 * @param agFilter The AG Grid date filter object containing type, operator, and conditions.
 * @return An array of Filter objects matching the API filtering structure.
 */
function convertAgDateItem(field: string, agFilter: DateFilterModel | ICombinedSimpleModel<DateFilterModel>): Filter[] {
  dayjs.extend(timezone);

  // Multiple conditions on filters
  if (isCombinedSimpleModel(agFilter)) {
    const conditions = agFilter.conditions.map(it => ({
      kind: FilterKind.DATE,
      field,
      dateFilter: substituteEqualsDateWithRange(it)
    }));

    if (agFilter.operator === 'AND') {
      return conditions;
    } else {
      return [orGroup(...conditions)];
    }
  }

  // Single condition
  return [{
    kind: FilterKind.DATE,
    field,
    dateFilter: substituteEqualsDateWithRange(agFilter)
  }];
}

/**
 * Parses a date string in the format 'YYYY-MM-DD HH:mm:ss' to an ISO 8601 string.
 * Returns null if the input is falsy.
 * @param date The date string to parse.
 * @return The parsed date as an ISO string, or null if input is invalid.
 */
function parseDate(date: string): string {
  return date
    ? dayjs.utc(date, 'YYYY-MM-DD HH:mm:ss').toISOString()
    : null;
}

/**
 * FIXME TTT-4210 Remove after Ag-Grid is updated and allows a timepicker in the date filter
 * Ag-Grid v29.x does not provide a timepicker input for its date filter.
 * This function simulates equals filter on a date value with a DATE_BETWEEN
 * from the start of day to the end of day based on the user's timezone.
 */
function substituteEqualsDateWithRange(agFilter: DateFilterModel) {
  const parseAgFilterDate = (date: string): Dayjs => {
    return date
      ? dayjs(date, 'YYYY-MM-DD HH:mm:ss').tz(dayjs.tz.guess(), true)
      : null;
  };

  if (agFilter.type === 'equals') {
    return {
      type: DateFilterType.DATE_BETWEEN,
      dateFrom: parseAgFilterDate(agFilter.dateFrom)?.startOf('day').toISOString(),
      dateTo: parseAgFilterDate(agFilter.dateFrom)?.endOf('day').toISOString()
    };
  } else {
    return {
      type: dateFilterLookup[agFilter.type],
      dateFrom: parseAgFilterDate(agFilter.dateFrom)?.toISOString(),
      dateTo: parseAgFilterDate(agFilter.dateTo)?.toISOString()
    };
  }
}

/**
 * Type guard to check if a filter is an instance of ICombinedSimpleModel
 * @param obj
 */
export function isCombinedSimpleModel(obj: any): obj is ICombinedSimpleModel<any> {
  return obj && 'operator' in obj && 'conditions' in obj;
}
